import React, { useState, useEffect } from 'react';
import { Flame, Copy } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const FLASH_MS = 48 * 60 * 60 * 1000; // 48 hours
const LS_KEY = 'plixy_flash_start';
const COUPON = 'FLASH50';

const getRollingStart = () => {
  const now = Date.now();
  const stored = parseInt(localStorage.getItem(LS_KEY) || '0', 10);
  if (!stored || now - stored >= FLASH_MS) {
    localStorage.setItem(LS_KEY, String(now));
    return now;
  }
  return stored;
};

const format = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return {
    h: String(h).padStart(2, '0'),
    m: String(m).padStart(2, '0'),
    s: String(s).padStart(2, '0'),
  };
};

const CountdownBar = () => {
  const [start, setStart] = useState(getRollingStart);
  const [now, setNow] = useState(Date.now());
  const { toast } = useToast();

  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
      // auto-reset if elapsed
      const s = parseInt(localStorage.getItem(LS_KEY) || '0', 10);
      if (!s || Date.now() - s >= FLASH_MS) {
        const fresh = getRollingStart();
        setStart(fresh);
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = FLASH_MS - (now - start);
  const t = format(remaining);

  const copyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(COUPON);
      toast({ title: 'Coupon copied!', description: `Use ${COUPON} at checkout for 50% off.` });
    } catch {}
  };

  const goPricing = (e) => {
    e.preventDefault();
    if (window.location.pathname !== '/') {
      window.location.href = '/#pricing';
      return;
    }
    const el = document.getElementById('pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="w-full plixy-gradient-ring text-white">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2 md:gap-4 text-xs md:text-sm font-semibold flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <Flame className="w-4 h-4 text-amber-200" />
          <span className="font-extrabold uppercase tracking-wider">Flash Sale</span>
          <span className="hidden sm:inline opacity-90">— 50% OFF annual plans</span>
        </span>
        <button onClick={copyCoupon} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 transition-colors border border-white/25 backdrop-blur-sm font-mono font-bold">
          {COUPON} <Copy className="w-3 h-3" />
        </button>
        <span className="inline-flex items-center gap-1.5 font-mono">
          <TimeCell v={t.h} label="hr" />
          <span className="opacity-70">:</span>
          <TimeCell v={t.m} label="min" />
          <span className="opacity-70">:</span>
          <TimeCell v={t.s} label="sec" />
        </span>
        <a href="#pricing" onClick={goPricing} className="hidden md:inline-flex items-center gap-1 px-3 py-1 rounded-md bg-white text-[#0a1930] font-bold hover:opacity-90 transition-opacity">
          Claim now →
        </a>
      </div>
    </div>
  );
};

const TimeCell = ({ v, label }) => (
  <span className="inline-flex items-baseline gap-1">
    <span className="tabular-nums font-extrabold">{v}</span>
    <span className="text-[10px] uppercase opacity-70">{label}</span>
  </span>
);

export default CountdownBar;
